'use client'

/**
 * Per-question score shown inside the exam view. Reads the exam-review context:
 * - review mode (student): read-only "earned / max pts" pill + any teacher
 *   feedback for this question.
 * - grade mode (teacher): editable points (override) + revert-to-auto + a
 *   feedback text box.
 * Shows points (Punkte), not the 1-6 grade. Renders nothing when no review is
 * active for this component.
 */

import { useEffect, useRef, useState } from 'react'
import { RotateCcw, Regex } from 'lucide-react'
import { cn } from '@/lib/utils'
import { extractCriterionRegex, stripInlineRegex } from '@/lib/scoring/regex-check'
import { isPointsInRange } from '@/lib/scoring/score-component'
import { useComponentReview } from '@/contexts/exam-review-context'

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1))
// Mirror the teacher panel's tints: your score (plain) | rubric (blue).
const RUBRIC_BG = 'bg-sky-50 dark:bg-sky-950/30'

export function ScoreBadge({ componentId }: { componentId: string }) {
  const { active, mode, review, setOverride, setFeedback } = useComponentReview(componentId)
  const [draft, setDraft] = useState('')
  const [fb, setFb] = useState('')
  // While a field is focused, DON'T overwrite its draft from a server reload:
  // the debounced save triggers a /review refetch, and syncing the (lagging)
  // server value back into a field you're typing in dropped your last letters.
  const ptsFocused = useRef(false)
  const fbFocused = useRef(false)

  useEffect(() => {
    if (!review || ptsFocused.current) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync the editable draft to the loaded/overridden score
    setDraft(fmt(review.earned))
  }, [review?.earned, review?.componentId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!review || fbFocused.current) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync feedback draft to loaded value
    setFb(review.feedback ?? '')
  }, [review?.feedback, review?.componentId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist points edits on a short debounce, not just on blur — the number
  // input's spinner arrows (and ↑/↓ arrow keys) change the value WITHOUT
  // blurring, so a blur-only commit dropped those edits (no override, lost on
  // reload). Skips an empty field (revert is handled on blur) and a no-op equal
  // to the current score (avoids a commit loop with the sync effect above).
  useEffect(() => {
    if (!review || draft === '') return
    const v = Number(draft)
    if (!Number.isFinite(v) || v === review.earned || !isPointsInRange(v, review.max)) return
    const t = setTimeout(() => setOverride(v), 400)
    return () => clearTimeout(t)
  }, [draft]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist feedback on a debounce (typing). No-op when unchanged from the
  // loaded value (also breaks the save→reload→sync loop).
  useEffect(() => {
    if (!review) return
    if (fb === (review.feedback ?? '')) return
    const t = setTimeout(() => setFeedback(fb.trim() === '' ? null : fb), 600)
    return () => clearTimeout(t)
  }, [fb]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!active || !review) return null

  const tone = review.earned >= review.max ? 'border-green-500/40 bg-green-500/10' : 'border-amber-500/40 bg-amber-500/10'

  if (mode === 'review') {
    // Read-only per-criterion breakdown — the same view the teacher edits, but the
    // student can't change it. Effective value per criterion = override ?? AI.
    const aiCrit = new Map(
      (((review.sources?.find((s) => s.source === 'ai')?.meta as { criteria?: { id: string; points: number; comment?: string }[] } | null)?.criteria) ?? []).map((c) => [c.id, c]),
    )
    const ovCrit = new Map(
      (((review.sources?.find((s) => s.source === 'override')?.meta as { criteria?: { id: string; points?: number; comment?: string }[] } | null)?.criteria) ?? []).map((c) => [c.id, c]),
    )
    const rubricCriteria = review.rubric?.criteria ?? []
    const showBreakdown = rubricCriteria.length > 0 && (aiCrit.size > 0 || ovCrit.size > 0)
    const rubricSum = Math.round(rubricCriteria.reduce((s, c) => s + (Number(c.points) || 0), 0) * 10) / 10
    return (
      // not-prose: the exam content is rendered inside `.prose`, which would force
      // a large font-size onto our <p>/<span> text — strip it so our sizing applies.
      <div className="mt-2 space-y-1.5 not-prose">
        {showBreakdown ? (
          // Same two-column mask the teacher edits — read-only for the student.
          <div className="overflow-hidden rounded-md border">
            <div className="flex border-b text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <div className="w-9 shrink-0 border-r" />
              <div className="flex-1 px-2 py-1">Your score</div>
              <div className={cn('flex-1 px-2 py-1', RUBRIC_BG)}>Rubric</div>
            </div>
            {rubricCriteria.map((rc, i) => {
              const ov = ovCrit.get(rc.id)
              const ai = aiCrit.get(rc.id)
              const points = ov?.points ?? ai?.points ?? null
              const comment = ov?.comment ?? ai?.comment ?? null
              return (
                <div key={rc.id} className={cn('flex items-stretch', i > 0 && 'border-t')}>
                  {/* ID gutter — vertically-centred criterion id down the left edge. */}
                  <div className="flex w-9 shrink-0 items-center justify-center border-r font-mono text-sm font-bold text-muted-foreground">
                    {rc.id}
                  </div>
                  {/* LEFT — your comment + points (small "regex" tag under the score) */}
                  <div className="flex flex-1 items-start gap-1.5 px-2 py-1 text-xs">
                    <p className="my-0! min-w-0 flex-1 whitespace-pre-wrap">
                      {comment ?? <span className="text-muted-foreground">—</span>}
                    </p>
                    <span className="flex w-10 shrink-0 flex-col items-end gap-0.5">
                      <span className="font-medium tabular-nums">{points == null ? '–' : fmt(points)}</span>
                      {extractCriterionRegex(rc.description) && (
                        <span
                          className={cn(
                            'inline-flex items-center gap-0.5 text-[9px] font-medium leading-none',
                            (points ?? 0) > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
                          )}
                        >
                          <Regex className="h-2.5 w-2.5" /> regex
                        </span>
                      )}
                    </span>
                  </div>
                  {/* RIGHT — the rubric criterion (regex annotation stripped for the student) */}
                  <div className={cn('flex flex-1 items-start gap-1.5 px-2 py-1 text-xs', RUBRIC_BG)}>
                    <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">/ {fmt(rc.points)}</span>
                    <p className="my-0! min-w-0 flex-1">
                      <span className="mr-1 font-mono text-[10px] text-muted-foreground">{rc.id}</span>
                      {stripInlineRegex(rc.description)}
                    </p>
                  </div>
                </div>
              )
            })}
            <div className="flex border-t text-xs">
              <div className="w-9 shrink-0 border-r" />
              <div className="flex flex-1 items-center justify-end gap-1.5 px-2 py-1">
                <span className="w-10 text-right font-semibold tabular-nums">Σ {fmt(review.earned)}</span>
              </div>
              <div className={cn('flex flex-1 items-center gap-1.5 px-2 py-1', RUBRIC_BG)}>
                <span className="w-10 text-right font-semibold tabular-nums">/ {fmt(rubricSum)}</span>
                <span className="text-muted-foreground">pts</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-medium ${tone}`}>
            <span className="tabular-nums">{fmt(review.earned)} / {fmt(review.max)}</span>
            <span className="text-muted-foreground font-normal">pts</span>
          </div>
        )}
        {review.feedback && (
          <div className="rounded-md border border-border bg-muted/40 px-2 py-1 text-xs">
            <span className="font-medium text-muted-foreground">Feedback</span>
            <p className="mt-0.5 whitespace-pre-wrap">{review.feedback}</p>
          </div>
        )}
      </div>
    )
  }

  // grade mode
  const commit = () => {
    const v = draft === '' ? null : Number(draft)
    if (v !== null && !isPointsInRange(v, review.max)) return
    if (v !== review.earned) setOverride(v)
  }
  // Out-of-range values are never saved; the field shows a hint instead.
  const outOfRange = draft !== '' && !isPointsInRange(Number(draft), review.max)
  // What clearing the override falls back to: AI score if present, else check.
  const underlying = review.aiEarned ?? review.autoEarned
  const underlyingLabel = review.aiEarned != null ? 'AI' : 'auto'
  return (
    <div className="mt-2 flex items-start gap-2">
      {/* Fixed width so the feedback box doesn't shift as the points value /
          auto-vs-revert affordance changes the badge's natural width. */}
      <div className="w-44 shrink-0 space-y-1">
        <div className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm ${tone}`}>
          <span className="text-xs text-muted-foreground">Points</span>
          <input
            type="number"
            step="0.1"
            min={0}
            max={review.max}
            aria-invalid={outOfRange}
            className={cn('w-12 h-7 rounded border bg-background px-1 text-right tabular-nums', outOfRange && 'border-destructive')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => { ptsFocused.current = true }}
            onBlur={() => { ptsFocused.current = false; commit() }}
          />
          <span className="text-muted-foreground tabular-nums">/ {fmt(review.max)}</span>
          {review.overridden ? (
            <button
              title={`Revert to ${underlyingLabel} (${fmt(underlying)})`}
              className="ml-auto text-muted-foreground hover:text-foreground"
              onClick={() => setOverride(null)}
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          ) : (
            <span className="ml-auto text-xs text-muted-foreground">{review.effectiveSource ?? 'auto'}</span>
          )}
        </div>
        {outOfRange && (
          <p className="px-0.5 text-[11px] text-destructive">Allowed: 0–{fmt(review.max)} pts (not saved)</p>
        )}
        {/* Per-source breakdown: the effective source is emphasised. */}
        <div className="flex items-center gap-2 px-0.5 text-[11px] text-muted-foreground tabular-nums">
          <span className={review.effectiveSource === 'check' ? 'font-semibold text-foreground' : ''}>
            check {fmt(review.autoEarned)}
          </span>
          {review.aiEarned != null && (
            <span className={review.effectiveSource === 'ai' ? 'font-semibold text-foreground' : ''}>
              AI {fmt(review.aiEarned)}
            </span>
          )}
        </div>
      </div>
      <textarea
        value={fb}
        onChange={(e) => setFb(e.target.value)}
        onFocus={() => { fbFocused.current = true }}
        onBlur={() => { fbFocused.current = false }}
        placeholder="Feedback for this question (shown to the student)…"
        rows={2}
        className="flex-1 min-w-0 rounded border bg-background px-2 py-1 text-sm resize-y"
      />
    </div>
  )
}
